import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  X,
  BarChart3,
  Monitor,
  Smartphone,
  Tablet,
  Bot,
  HelpCircle,
  Globe,
  MousePointerClick,
  ExternalLink,
  Calendar,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import Button from '../../components/ui/Button';
import {
  getShortLinkAnalytics,
  ShortLink,
  ShortLinkAnalytics,
} from '../../services/shortLinkService';

interface Props {
  link: ShortLink | null;
  onClose: () => void;
}

const DEVICE_ICONS: Record<string, React.ReactNode> = {
  desktop: <Monitor className="w-4 h-4" />,
  mobile: <Smartphone className="w-4 h-4" />,
  tablet: <Tablet className="w-4 h-4" />,
  bot: <Bot className="w-4 h-4" />,
  unknown: <HelpCircle className="w-4 h-4" />,
};

// Helper: barra horizontal proporcional ao maior valor da lista.
const Bar: React.FC<{ value: number; max: number; color?: string }> = ({ value, max, color = 'bg-primary-500' }) => {
  const pct = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
      <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
    </div>
  );
};

const ShortLinkAnalyticsModal: React.FC<Props> = ({ link, onClose }) => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<ShortLinkAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [days, setDays] = useState<7 | 30 | 90>(30);
  // Página da seção "Últimos cliques". Reseta quando muda window ou link.
  const [recentPage, setRecentPage] = useState(1);
  const RECENT_LIMIT = 20;

  // Reset page quando muda janela ou link
  useEffect(() => {
    setRecentPage(1);
  }, [link, days]);

  useEffect(() => {
    if (!link) return;
    const ac = new AbortController();
    setIsLoading(true);
    getShortLinkAnalytics(link.id, { days, recentPage, recentLimit: RECENT_LIMIT })
      .then((res) => {
        if (!ac.signal.aborted) setData(res.data);
      })
      .catch((err) => {
        if (!ac.signal.aborted) {
          toast.error(err?.response?.data?.message || t('shortLinks.analytics.loadError', 'Erro ao carregar analytics'));
        }
      })
      .finally(() => {
        if (!ac.signal.aborted) setIsLoading(false);
      });
    return () => ac.abort();
  }, [link, days, recentPage, t]);

  if (!link) return null;

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString(i18n.language || undefined, {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  const formatDay = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString(i18n.language || undefined, { day: '2-digit', month: 'short' });
    } catch {
      return iso;
    }
  };

  const buttonMax = Math.max(0, ...(data?.by_button.map((b) => b.count) || [0]));
  const deviceMax = Math.max(0, ...(data?.by_device.map((b) => b.count) || [0]));
  const refererMax = Math.max(0, ...(data?.by_referer.map((b) => b.count) || [0]));
  const utmMax = Math.max(0, ...(data?.by_utm.map((b) => b.count) || [0]));
  const countryMax = Math.max(0, ...(data?.by_country.map((b) => b.count) || [0]));

  // Pico do time series pra escala vertical do mini-gráfico
  const tsMax = Math.max(1, ...(data?.time_series.map((p) => p.count) || [1]));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 flex items-center justify-center flex-shrink-0">
              <BarChart3 className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                {link.title || link.code}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-mono truncate">/s/{link.code}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10) as 7 | 30 | 90)}
              className="text-sm px-2 py-1 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200"
            >
              <option value={7}>{t('shortLinks.analytics.last7', 'Últimos 7 dias')}</option>
              <option value={30}>{t('shortLinks.analytics.last30', 'Últimos 30 dias')}</option>
              <option value={90}>{t('shortLinks.analytics.last90', 'Últimos 90 dias')}</option>
            </select>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              aria-label={t('common.close', 'Fechar') as string}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body scrollable */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {isLoading ? (
            <div className="py-12 text-center text-gray-500 flex items-center justify-center gap-2">
              <RefreshCw className="w-4 h-4 animate-spin" />
              {t('common.loading', 'Carregando...')}
            </div>
          ) : !data ? (
            <div className="py-12 text-center text-gray-500">
              {t('shortLinks.analytics.noData', 'Sem dados ainda')}
            </div>
          ) : (
            <>
              {/* Total + time series */}
              <section className="bg-gradient-to-br from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 rounded-xl p-4 border border-primary-100 dark:border-primary-800/30">
                <div className="flex items-baseline justify-between mb-3">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {t('shortLinks.analytics.totalClicks', 'Cliques totais')}
                    </p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white tabular-nums">
                      {data.total.toLocaleString()}
                    </p>
                  </div>
                  <span className="text-xs text-gray-500 inline-flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {t('shortLinks.analytics.windowLabel', 'Janela: {{n}} dias').replace('{{n}}', String(data.period_days))}
                  </span>
                </div>
                {/* Gráfico de série temporal — line + área + dots interativos */}
                {data.time_series.length > 0 ? (() => {
                  // Densifica o período: cria 1 ponto por dia, preenchendo zeros nos
                  // dias sem clique. Sem isso o eixo X fica enganoso (pula gaps).
                  const dayMap = new Map(data.time_series.map(p => [String(p.day).slice(0, 10), p.count]));
                  const endDate = new Date();
                  endDate.setHours(0, 0, 0, 0);
                  const dense: Array<{ day: string; count: number }> = [];
                  for (let i = data.period_days - 1; i >= 0; i--) {
                    const d = new Date(endDate.getTime() - i * 86400000);
                    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                    dense.push({ day: key, count: dayMap.get(key) || 0 });
                  }
                  const denseMax = Math.max(1, ...dense.map(p => p.count));
                  const W = 800;
                  const H = 180;
                  const PAD_L = 32;
                  const PAD_R = 12;
                  const PAD_T = 12;
                  const PAD_B = 24;
                  const innerW = W - PAD_L - PAD_R;
                  const innerH = H - PAD_T - PAD_B;
                  // Coordenadas por ponto
                  const pts = dense.map((p, i) => ({
                    x: PAD_L + (dense.length > 1 ? (i / (dense.length - 1)) * innerW : innerW / 2),
                    y: PAD_T + innerH - (p.count / denseMax) * innerH,
                    day: p.day,
                    count: p.count,
                  }));
                  const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
                  const areaPath = `${linePath} L ${pts[pts.length - 1].x.toFixed(1)} ${(PAD_T + innerH).toFixed(1)} L ${pts[0].x.toFixed(1)} ${(PAD_T + innerH).toFixed(1)} Z`;
                  // Y axis: 3 ticks (0, meio, max)
                  const yTicks = [0, Math.round(denseMax / 2), denseMax];
                  // X axis: primeira, meio, última datas
                  const xTickIdxs = dense.length >= 3
                    ? [0, Math.floor(dense.length / 2), dense.length - 1]
                    : Array.from({ length: dense.length }, (_, i) => i);

                  return (
                    <div className="w-full">
                      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-44 overflow-visible" role="img">
                        <defs>
                          <linearGradient id="ts-area" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="currentColor" stopOpacity="0.25" className="text-primary-500" />
                            <stop offset="100%" stopColor="currentColor" stopOpacity="0" className="text-primary-500" />
                          </linearGradient>
                        </defs>
                        {/* Grid horizontal */}
                        {yTicks.map((v, i) => {
                          const y = PAD_T + innerH - (v / denseMax) * innerH;
                          return (
                            <g key={`y-${i}`}>
                              <line
                                x1={PAD_L} y1={y} x2={W - PAD_R} y2={y}
                                stroke="currentColor"
                                strokeWidth="1"
                                strokeDasharray="2 4"
                                className="text-gray-200 dark:text-gray-700"
                              />
                              <text
                                x={PAD_L - 6} y={y}
                                textAnchor="end"
                                dominantBaseline="middle"
                                className="fill-gray-500 dark:fill-gray-400 text-[10px] tabular-nums"
                              >
                                {v}
                              </text>
                            </g>
                          );
                        })}
                        {/* Área + linha */}
                        <path d={areaPath} fill="url(#ts-area)" className="text-primary-500" />
                        <path
                          d={linePath}
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="text-primary-500"
                        />
                        {/* Dots: visíveis nos pontos não-zero */}
                        {pts.map((p, i) => (
                          p.count > 0 ? (
                            <g key={`dot-${i}`}>
                              <circle
                                cx={p.x} cy={p.y} r="3"
                                fill="white"
                                stroke="currentColor"
                                strokeWidth="2"
                                className="text-primary-500"
                              />
                              <title>{`${formatDay(p.day)}: ${p.count}`}</title>
                            </g>
                          ) : null
                        ))}
                        {/* X axis labels */}
                        {xTickIdxs.map((i) => (
                          <text
                            key={`x-${i}`}
                            x={pts[i].x}
                            y={H - 6}
                            textAnchor={i === 0 ? 'start' : i === dense.length - 1 ? 'end' : 'middle'}
                            className="fill-gray-500 dark:fill-gray-400 text-[10px]"
                          >
                            {formatDay(dense[i].day)}
                          </text>
                        ))}
                      </svg>
                    </div>
                  );
                })() : (
                  <p className="text-xs text-gray-500 italic">{t('shortLinks.analytics.noTimeData', 'Sem cliques no período.')}</p>
                )}
              </section>

              {/* Por botão (só pra interstitial) */}
              {link.link_type === 'interstitial' && data.by_button.length > 0 && (
                <section>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                    <MousePointerClick className="w-4 h-4" />
                    {t('shortLinks.analytics.byButton', 'Cliques por botão')}
                  </h3>
                  <div className="space-y-2">
                    {data.by_button.map((b) => (
                      <div key={b.button_index} className="flex items-center gap-3">
                        <div className="w-40 flex-shrink-0 text-sm text-gray-700 dark:text-gray-300 truncate" title={b.button_label || ''}>
                          {b.button_label || `Botão ${b.button_index + 1}`}
                        </div>
                        <Bar value={b.count} max={buttonMax} />
                        <div className="w-12 text-right text-sm font-medium text-gray-900 dark:text-white tabular-nums">
                          {b.count}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Grid: devices + países */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {data.by_device.length > 0 && (
                  <section>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                      {t('shortLinks.analytics.byDevice', 'Dispositivos')}
                    </h3>
                    <div className="space-y-2">
                      {data.by_device.map((d) => (
                        <div key={d.device_type} className="flex items-center gap-3">
                          <div className="w-32 flex-shrink-0 text-sm text-gray-700 dark:text-gray-300 inline-flex items-center gap-2">
                            {DEVICE_ICONS[d.device_type] || DEVICE_ICONS.unknown}
                            <span className="capitalize">{t(`shortLinks.analytics.device.${d.device_type}`, d.device_type)}</span>
                          </div>
                          <Bar value={d.count} max={deviceMax} color="bg-emerald-500" />
                          <div className="w-12 text-right text-sm font-medium text-gray-900 dark:text-white tabular-nums">
                            {d.count}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
                {data.by_country.length > 0 && (
                  <section>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      {t('shortLinks.analytics.byCountry', 'Países')}
                    </h3>
                    <div className="space-y-2">
                      {data.by_country.map((c) => (
                        <div key={c.country} className="flex items-center gap-3">
                          <div className="w-32 flex-shrink-0 text-sm text-gray-700 dark:text-gray-300 font-mono">{c.country}</div>
                          <Bar value={c.count} max={countryMax} color="bg-amber-500" />
                          <div className="w-12 text-right text-sm font-medium text-gray-900 dark:text-white tabular-nums">
                            {c.count}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>

              {/* Referers + UTM */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {data.by_referer.length > 0 && (
                  <section>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                      {t('shortLinks.analytics.byReferer', 'De onde vieram')}
                    </h3>
                    <div className="space-y-2">
                      {data.by_referer.map((r) => (
                        <div key={r.domain} className="flex items-center gap-3">
                          <div className="w-40 flex-shrink-0 text-sm text-gray-700 dark:text-gray-300 truncate" title={r.domain}>
                            {r.domain}
                          </div>
                          <Bar value={r.count} max={refererMax} color="bg-violet-500" />
                          <div className="w-12 text-right text-sm font-medium text-gray-900 dark:text-white tabular-nums">
                            {r.count}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
                {data.by_utm.length > 0 && (
                  <section>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                      {t('shortLinks.analytics.byUtm', 'Campanhas (UTM)')}
                    </h3>
                    <div className="space-y-2">
                      {data.by_utm.map((u, i) => (
                        <div key={`${u.source || ''}-${u.medium || ''}-${u.campaign || ''}-${i}`} className="flex items-center gap-3">
                          <div className="w-40 flex-shrink-0 text-xs text-gray-700 dark:text-gray-300 truncate">
                            {[u.source, u.medium, u.campaign].filter(Boolean).join(' · ') || '—'}
                          </div>
                          <Bar value={u.count} max={utmMax} color="bg-pink-500" />
                          <div className="w-12 text-right text-sm font-medium text-gray-900 dark:text-white tabular-nums">
                            {u.count}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>

              {/* Últimos cliques (paginado) */}
              {data.recent.length > 0 && (
                <section>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      {t('shortLinks.analytics.recent', 'Últimos cliques')}
                    </h3>
                    {data.recent_pagination && data.recent_pagination.totalPages > 1 && (
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <span>
                          {t('shortLinks.analytics.pageInfo', '{{page}} de {{total}}')
                            .replace('{{page}}', String(data.recent_pagination.page))
                            .replace('{{total}}', String(data.recent_pagination.totalPages))}
                        </span>
                        <button
                          type="button"
                          onClick={() => setRecentPage((p) => Math.max(1, p - 1))}
                          disabled={data.recent_pagination.page <= 1 || isLoading}
                          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
                          aria-label={t('common.previous', 'Anterior') as string}
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setRecentPage((p) => p + 1)}
                          disabled={!data.recent_pagination.hasMore || isLoading}
                          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
                          aria-label={t('common.next', 'Próximo') as string}
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        <tr className="border-b border-gray-100 dark:border-gray-800">
                          <th className="text-left py-2 px-2">{t('shortLinks.analytics.colWhen', 'Quando')}</th>
                          <th className="text-left py-2 px-2">{t('shortLinks.analytics.colButton', 'Botão')}</th>
                          <th className="text-left py-2 px-2">{t('shortLinks.analytics.colDevice', 'Dispositivo')}</th>
                          <th className="text-left py-2 px-2">{t('shortLinks.analytics.colCountry', 'País')}</th>
                          <th className="text-left py-2 px-2">{t('shortLinks.analytics.colSource', 'Fonte')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                        {data.recent.map((c) => {
                          let refererDomain: string | null = null;
                          if (c.referer) {
                            try { refererDomain = new URL(c.referer).hostname.replace(/^www\./, ''); } catch { /* ignore */ }
                          }
                          const source = c.utm_source || refererDomain || (c.referer ? c.referer.slice(0, 30) : '—');
                          return (
                            <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                              <td className="py-2 px-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                {formatDate(c.clicked_at)}
                              </td>
                              <td className="py-2 px-2 text-gray-700 dark:text-gray-300">
                                {c.button_label
                                  ? (
                                    <span className="inline-flex items-center gap-1">
                                      {c.button_label}
                                      {c.target_url && (
                                        <a href={c.target_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-primary-600">
                                          <ExternalLink className="w-3 h-3" />
                                        </a>
                                      )}
                                    </span>
                                  )
                                  : <span className="text-gray-400">—</span>
                                }
                              </td>
                              <td className="py-2 px-2 text-gray-700 dark:text-gray-300 capitalize">
                                <span className="inline-flex items-center gap-1">
                                  {DEVICE_ICONS[c.device_type || 'unknown']}
                                  {c.browser || c.device_type}
                                </span>
                              </td>
                              <td className="py-2 px-2 text-gray-700 dark:text-gray-300 font-mono">{c.country || '—'}</td>
                              <td className="py-2 px-2 text-gray-500 dark:text-gray-400 truncate max-w-[200px]" title={c.referer || ''}>
                                {source}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
            </>
          )}
        </div>

        <div className="border-t border-gray-100 dark:border-gray-800 p-3 flex justify-end">
          <Button variant="secondary" onClick={onClose}>{t('common.close', 'Fechar')}</Button>
        </div>
      </div>
    </div>
  );
};

export default ShortLinkAnalyticsModal;
